import {
  CheckCircleOutlined,
  InboxOutlined,
  LoadingOutlined,
  StopOutlined
} from "@ant-design/icons"
import { Alert, App, Button, Modal, Typography } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { getResponseMessage } from "../../../utils/handlers/get-response-message.ts"

import {
  ARCHIVE_ACKNOWLEDGEMENT,
  UNPAID_INVOICES_ACKNOWLEDGEMENT,
  pendingCommitmentsFrom,
  unpaidInvoicesFrom
} from "../api/pending-commitments.ts"
import { archiveProject, patchProject } from "../api/services.ts"
import type { ArchiveQuestion } from "../api/services.types.ts"
import {
  PROJECT_STATUSES,
  isArchivingStatus
} from "../constants/project-statuses.ts"
import { PROJECTS_SAVE_RESULT } from "../constants/projects-save-results.ts"
import { archiveQuestionLabels } from "../helpers/archive-question-labels.ts"
import { PendingCommitmentsAlert } from "./PendingCommitmentsAlert.tsx"
import {
  type ClosingStatus,
  UnpaidInvoicesDialog
} from "./UnpaidInvoicesDialog.tsx"

const { Text } = Typography

interface ArchiveProjectModalProps {
  projectId: string
  projectName?: string | null
  // Undefined only for a caller that hasn't loaded the row yet — treated as
  // "unknown, don't block" rather than "not archivable", so the modal never
  // opens pre-blocked on a still-loading status.
  projectStatus?: string | number | null
  // Completing or cancelling takes its own right; without it the two choices
  // are shown but cannot be pressed.
  canClose?: boolean
  open: boolean
  onCancel: () => void
  onArchived?: () => void
}

interface Acknowledged {
  meetings: boolean
  unpaid: boolean
}

interface ResponseDetails {
  type?: string
}

export function ArchiveProjectModal({
  projectId,
  projectName,
  projectStatus,
  canClose = false,
  open,
  onCancel,
  onArchived
}: ArchiveProjectModalProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [saving, setSaving] = useState(false)
  // Set when the server refuses because the project still has meetings booked
  // or signature requests out.
  const [question, setQuestion] = useState<ArchiveQuestion | null>(null)
  // The status the user picked to close the project with; a confirmed
  // meetings question resends it.
  const [closingStatus, setClosingStatus] = useState<ClosingStatus | null>(null)
  // A refusal the user can act on (unpaid invoices, a rule the server keeps),
  // shown in the dialog rather than in a toast that disappears.
  const [refusal, setRefusal] = useState<string | null>(null)
  // Set when the server asks about unpaid invoices before filing the project;
  // once the user agrees, every later request in this dialog carries it.
  const [unpaidCount, setUnpaidCount] = useState<number | null>(null)
  const [unpaidAcknowledged, setUnpaidAcknowledged] = useState(false)

  // Mirrors the server's own precondition (apps/core/project_archive.py,
  // reason_for_status). `== null` covers both undefined (not loaded yet) and
  // null (genuinely unknown) without blocking either.
  const needsClosing =
    projectStatus != null && !isArchivingStatus(projectStatus)

  const handleRefusal = (details: ResponseDetails | undefined) => {
    const owed = unpaidInvoicesFrom(details)
    if (owed) {
      setUnpaidCount(owed.unpaidInvoiceCount)
      return
    }
    const asked = pendingCommitmentsFrom(details)
    if (asked) {
      setQuestion(asked)
      return
    }
    // Leaving confirm mode up would pin the modal to a list that no longer
    // describes the state and keep re-sending the confirmation.
    setQuestion(null)
    // A refused PATCH arrives in the error envelope, its text at
    // `details.message` with `message` an object; read it as a string only.
    setRefusal(
      getResponseMessage(
        details,
        details?.type === PROJECTS_SAVE_RESULT.PENDING_COMMITMENTS
          ? t("projectArchive.commitments.unavailable")
          : t("projectArchive.failed")
      )
    )
  }

  const archive = async ({ meetings, unpaid }: Acknowledged) => {
    setSaving(true)
    setRefusal(null)
    const response = await archiveProject({
      id: projectId,
      ...(meetings ? { cancel_appointments: true } : {}),
      ...(unpaid ? UNPAID_INVOICES_ACKNOWLEDGEMENT : {})
    })
    setSaving(false)

    if (response.ok) {
      setQuestion(null)
      message.success(t("projectArchive.success"))
      onArchived?.()
      return
    }
    handleRefusal(response.data as ResponseDetails | undefined)
  }

  // Completing or cancelling a project files it in the Archive on the server,
  // so one status change is the whole action.
  const close = async (
    status: ClosingStatus,
    { meetings, unpaid }: Acknowledged
  ) => {
    setClosingStatus(status)
    setSaving(true)
    setRefusal(null)
    const response = await patchProject(projectId, {
      project_status: status,
      ...(meetings ? ARCHIVE_ACKNOWLEDGEMENT : {}),
      ...(unpaid ? UNPAID_INVOICES_ACKNOWLEDGEMENT : {})
    })
    setSaving(false)

    if (response.ok) {
      setQuestion(null)
      message.success(
        status === PROJECT_STATUSES.COMPLETED
          ? t("projectArchive.close.completedDone")
          : t("projectArchive.close.cancelledDone")
      )
      onArchived?.()
      return
    }
    handleRefusal(response.data as ResponseDetails | undefined)
  }

  const displayName = projectName?.trim() || t("projectArchive.thisProject")
  const labels = question
    ? archiveQuestionLabels(question, "projectArchive", t)
    : null

  const confirm = () => {
    const acknowledged = { meetings: !!question, unpaid: unpaidAcknowledged }
    if (needsClosing) {
      if (closingStatus && question) return close(closingStatus, acknowledged)
      return
    }
    return archive(acknowledged)
  }

  const acknowledgeUnpaid = () => {
    setUnpaidAcknowledged(true)
    setUnpaidCount(null)
    const acknowledged = { meetings: false, unpaid: true }
    if (needsClosing && closingStatus) return close(closingStatus, acknowledged)
    return archive(acknowledged)
  }

  // Before a choice is made the closing dialog has nothing to confirm; once
  // the server asks about meetings, its confirm resends the chosen status.
  const showConfirm = !needsClosing || !!question

  const choices: {
    status: ClosingStatus
    icon: React.ReactNode
    tone: string
    title: string
    body: string
  }[] = [
    {
      status: PROJECT_STATUSES.COMPLETED,
      icon: <CheckCircleOutlined />,
      tone: "bg-primary/10 text-primary",
      title: t("projectArchive.close.complete"),
      body: t("projectArchive.close.completeBody")
    },
    {
      status: PROJECT_STATUSES.CANCELLED,
      icon: <StopOutlined />,
      tone: "bg-slate-100 text-slate-600",
      title: t("projectArchive.close.cancel"),
      body: t("projectArchive.close.cancelBody")
    }
  ]

  return (
    <Modal
      open={open}
      centered
      width={520}
      title={t("projectArchive.title", { name: displayName })}
      footer={(_, { OkBtn, CancelBtn }) => (
        <>
          <CancelBtn />
          {showConfirm && <OkBtn />}
        </>
      )}
      okText={labels ? labels.confirm : t("projectArchive.confirm")}
      cancelText={
        labels
          ? t("projectArchive.commitments.keepActive")
          : needsClosing
            ? t("projectArchive.close.keepActive")
            : t("projectArchive.cancel")
      }
      confirmLoading={saving}
      cancelButtonProps={{ disabled: saving }}
      closable={!saving}
      keyboard={!saving}
      maskClosable={false}
      onOk={confirm}
      onCancel={onCancel}
      destroyOnHidden
    >
      {needsClosing ? (
        <div className="mb-4 flex flex-col gap-3">
          <Text type="secondary">{t("projectArchive.close.prompt")}</Text>
          {choices.map((choice) => {
            const busy = saving && closingStatus === choice.status
            const picked = !!question && closingStatus === choice.status
            return (
              <Button
                key={choice.status}
                type="default"
                block
                disabled={!canClose || saving || !!question}
                onClick={() =>
                  close(choice.status, {
                    meetings: false,
                    unpaid: unpaidAcknowledged
                  })
                }
                aria-pressed={picked}
                className={`!flex !h-auto !items-center !justify-start !gap-3 !whitespace-normal !rounded-xl !p-3 !text-left ${
                  picked ? "!border-primary" : ""
                }`}
              >
                <span
                  className={`flex size-10 shrink-0 items-center justify-center rounded-lg text-lg ${choice.tone}`}
                  aria-hidden
                >
                  {busy ? <LoadingOutlined /> : choice.icon}
                </span>
                <span className="flex min-w-0 flex-col">
                  <span className="font-semibold">{choice.title}</span>
                  <span className="text-xs text-gray-500">{choice.body}</span>
                </span>
              </Button>
            )
          })}
          {!canClose && (
            <Text type="secondary" className="text-xs">
              {t("projectArchive.closeBlocked")}
            </Text>
          )}
        </div>
      ) : (
        <div className="mb-4 flex items-start gap-3 rounded-xl bg-slate-50 p-3">
          <span
            className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-lg text-primary"
            aria-hidden
          >
            <InboxOutlined />
          </span>
          <Text>{t("projectArchive.impact")}</Text>
        </div>
      )}

      {refusal && (
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          data-testid="archive-refusal"
          message={refusal}
        />
      )}

      {unpaidCount != null && (
        <UnpaidInvoicesDialog
          open
          mode="close"
          count={unpaidCount}
          status={closingStatus ?? (projectStatus as ClosingStatus)}
          busy={saving}
          onClose={() => setUnpaidCount(null)}
          onConfirm={acknowledgeUnpaid}
        />
      )}

      {question && (
        <PendingCommitmentsAlert
          type="warning"
          commitments={question.pendingCommitments}
          signatures={question.pendingSignatures}
        />
      )}
    </Modal>
  )
}
